import { PreferenceService } from '../lib/das-api/api/preference.service';
import { BehaviorSubject, merge, of } from 'rxjs';
import { ResourcesService } from '../lib/das-api/api/resources.service';
import { ContainerName } from '../lib/das-api/model/containerName';
import { ImageSize } from '../lib/das-api/model/imageSize';
import { WBGetStartedImages } from '../lib/das-api/model/wBGetStartedImages';
import { TGetStartedImage } from '../models/GetStartedImage.model';
import { fromNullable, none, some } from 'fp-ts/lib/Option';
import { RemoteDataUtils } from '@assetmark/ui-kit/dist/utils/remote-data.utils';
import { TAddUpdateLeadName } from '../models/add-update-lead-request';
import { LeadService } from '../lib/das-api/api/lead.service';
import { LeadProfileResponse } from '../lib/das-api/model/leadProfileResponse';
import { TLeadProfileResponse } from '../models/lead-profile-response';
import { ObjectUtils } from '@assetmark/ui-kit/dist/utils/object.utils';
import { ArrayUtils } from '@assetmark/ui-kit/dist/utils/array.utils';
import { LeadStrategyInfoResponse } from '../lib/das-api/model/leadStrategyInfoResponse';
import { TLeadStrategyInfoResponse } from '../models/lead-strategy-info-response';
import { AppApi } from './app.api';
import { ABOUT_YOU_KEY, ADVISOR_ID_KEY, LEAD_APP_STATE, LEAD_ID_KEY, LEAD_STRATEGY_ID_KEY } from '../constants/storage.constant';
import { LocalStorageUtils } from '@assetmark/ui-kit/dist/utils/local-storage.utils';
import { TUpdateLeadStrategy } from '../models/update-lead';
import { defaultLeadProfile } from '../constants/data.constant';
import { TAmountReason } from '../models/amount-reason';
import { TLastPageRequest } from '../models/last-update-page-request';
import { getLastVisitedPage, getRedirectPage } from '../helpers/pagehelper';
import { parseGoalTypeToEnum } from '../helpers/goal.helper';

const WEALTHBUILDER_PREVIEW_GetStarted_Image1 = 'WEALTHBUILDER_PREVIEW_GetStarted_Image1';
const WEALTHBUILDER_PREVIEW_GetStarted_Image2 = 'WEALTHBUILDER_PREVIEW_GetStarted_Image2';
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace LeadApi {
	export const showSpinner$ = new BehaviorSubject<boolean>(false);
	export const redirectToPage$ = new BehaviorSubject<string>('');
	export const leadProfile$ = new BehaviorSubject<TLeadProfileResponse>(defaultLeadProfile);
	export const leadStrategy$ = new BehaviorSubject<TAmountReason>({ investmentAmount: 0, investmentReason: '2' });
	export const getSavedLeadProfile$ = () => leadProfile$.asObservable(); 
	export const getSavedLeadStrategy$ = () => leadStrategy$.asObservable();
	export const getPageSetting = (advisorId: string) => {
		if (advisorId) {
			return PreferenceService.apiDasV1PreferenceAgentAgentIdPagesettingGet(advisorId)
			.mapRD(page => page ? ({
				image1: page[WEALTHBUILDER_PREVIEW_GetStarted_Image1] ? +page[WEALTHBUILDER_PREVIEW_GetStarted_Image1] : 1,
				image2: page[WEALTHBUILDER_PREVIEW_GetStarted_Image2] ? +page[WEALTHBUILDER_PREVIEW_GetStarted_Image2] : 4,
			}) : ({ image1: 1, image2: 4 }))
				.shareReplay(1);
		} else {
			return of(RemoteDataUtils.initial);
		}
	};

	export const getStartedImages = (screenSize: string) => {
		let imageSize: any;
		if (screenSize === 'Medium') {
			imageSize = ImageSize.Medium;
		} else if (screenSize === 'Small') {
			imageSize = ImageSize.Small;
		} else {
			imageSize = ImageSize.Large;
		}
		return ResourcesService.apiDasV1ResourcesImagesGet(ContainerName.Getstartedimages, imageSize)
			.mapRD(data => data.map(parseImage))
			.shareReplay(1);
	};

	export const lastPageVisited = (page: TLastPageRequest) => {
		const lastPage = getLastVisitedPage(page.lastPageVisited);
		LeadService.apiDasV1LeadLeadIdLeadstrategiesLeadStrategyIdApplicationstatePost(
			page.leadId,
			page.leadStrategyId,
			{ agentId: page.agentId, lastPageVisited: lastPage },
		).doRD(data => LocalStorageUtils.setItem(LEAD_APP_STATE)(data))
		.subscribe();
	};

	export const updateLastVisitedPage = (page: TLastPageRequest, goalType = "") => {
		const lastPage = getLastVisitedPage(page.lastPageVisited);
        LeadService.apiDasV1LeadLeadIdLeadstrategiesLeadStrategyIdApplicationstatePost(page.leadId, page.leadStrategyId, { agentId: page.agentId, lastPageVisited: lastPage })
        .doRD(data => LocalStorageUtils.setItem(LEAD_APP_STATE)(data))
        .subscribe(data => {
            if(data.isSuccess()) {
                redirectToPage$.next(getRedirectPage(page.currentRoute, goalType));
				showSpinner$.next(false);
            }
        });
    };
	
	export const addLeadAbout = (lead: TAddUpdateLeadName, lastPage: TLastPageRequest) => {
		showSpinner$.next(true);
		const response$ = LeadService.apiDasV1LeadPost(lead)
			.doRD(data => {
				LocalStorageUtils.setItem(ABOUT_YOU_KEY)(data);
			})
			.doRD(data =>
				updateLastVisitedPage(
					{
						leadId: data.leadProfileId || '',
						leadStrategyId: data.leadStrategyId ? data.leadStrategyId[0] : '',
						agentId: data.agentId || '',
						lastPageVisited: lastPage.lastPageVisited,
						currentRoute: lastPage.currentRoute,
					},
				),
			)
			.mapRD(parseLead);

		response$.subscribe(data => {
			if (data.isSuccess()) {
				LocalStorageUtils.setItem(ADVISOR_ID_KEY)(data.value.agentId.getOrElse(''));
				LocalStorageUtils.setItem(LEAD_ID_KEY)(data.value.leadProfileId.getOrElse(''));
				LocalStorageUtils.setItem(LEAD_STRATEGY_ID_KEY)(data.value.leadStrategyId[0]);
				leadProfile$.next(data.value);
				//showSpinner$.next(false);
			}
		});
	};

	export const updateLeadAbout = (lead: TAddUpdateLeadName, lastPage: TLastPageRequest, leadId: string) => {
		showSpinner$.next(true);
		const response$ = LeadService.apiDasV1LeadLeadIdPut(leadId, lead)
			.doRD(data => {
				LocalStorageUtils.setItem(ABOUT_YOU_KEY)(data);
			})
			.doRD(data =>
				updateLastVisitedPage(
					{
						leadId: data.leadProfileId || '',
						leadStrategyId: data.leadStrategyId ? data.leadStrategyId[0] : '',
						agentId: data.agentId || '',
						lastPageVisited: lastPage.lastPageVisited,
						currentRoute: lastPage.currentRoute,
					},
				),
			)
			.mapRD(parseLead);

		response$.subscribe(data => {
			if (data.isSuccess()) {
				// LocalStorageUtils.setItem(ADVISOR_ID_KEY)(data.value.agentId.getOrElse(''));
				// LocalStorageUtils.setItem(LEAD_ID_KEY)(data.value.leadProfileId.getOrElse(''));
				// LocalStorageUtils.setItem(LEAD_STRATEGY_ID_KEY)(data.value.leadStrategyId[0]);
				leadProfile$.next(data.value);
				//showSpinner$.next(false);
			}
		});
	};

	export const updateLeadAmountAndReason = (lead: TUpdateLeadStrategy, lastPage: TLastPageRequest) => {
		showSpinner$.next(true);
		const response$ = LeadService.apiDasV1LeadLeadIdLeadstrategiesLeadStrategyIdPut(
			lead.leadId,
			lead.leadStrategyId,
			{
				agentId: lead.agentId,
				riskProfileId: lead.riskProfileId,
				investmentAmount: lead.investmentAmount,
				goalType: parseGoalTypeToEnum(lead.goalType),
			},
		)
			.doRD(data => {
				leadStrategy$.next({
					investmentAmount: lead.investmentAmount,
					investmentReason: String(lead.goalType),
				});
			})
			.doRD(data =>
				updateLastVisitedPage(
					{
						leadId: lead.leadId,
						leadStrategyId: lead.leadStrategyId,
						agentId: lead.agentId,
						lastPageVisited: lastPage.lastPageVisited,
						currentRoute: lastPage.currentRoute,
					},
					String(lead.goalType),
				),
			);
		// .mapRD(parseLead);
		response$.subscribe();
	};

	export const pageSetting$ = AppApi.advisorId$.switchMap(advisorId => getPageSetting(advisorId));
	
	export const sync$ = merge(pageSetting$);

	const parseImage = (img: WBGetStartedImages): TGetStartedImage => {
		return {
			imageUri: fromNullable(img.imageUri ? `/api/das/v1/Resources?url=${img.imageUri}` : null),
			index: fromNullable(img.index),
			imageSizeType: fromNullable(img.imageSizeType),
		};
	};
	export const parseLeadStrategy = (strategy: LeadStrategyInfoResponse): TLeadStrategyInfoResponse => {
		return {
			leadStrategyId: fromNullable(strategy.leadStrategyId),
			leadGoalId: fromNullable(strategy.leadGoalId),
			lastPageVisited: fromNullable(strategy.lastPageVisited),
			riskProfileId: fromNullable(strategy.riskProfileId),
			investmentAmount: fromNullable(strategy.investmentAmount),
			goalType: fromNullable(strategy.goalType),
			createdDate: fromNullable(strategy.createdDate),
			modifiedDate: fromNullable(strategy.modifiedDate),
			lead: ObjectUtils.isNotNullable(strategy.lead) ? some(parseLead(strategy.lead)) : none,
		};
	};
	export const parseLead = (lead: LeadProfileResponse): TLeadProfileResponse => {
		return {
			leadProfileId: fromNullable(lead.leadProfileId),
			leadStrategyId: ObjectUtils.isNotNullable(lead.leadStrategyId) ? lead.leadStrategyId : ArrayUtils.empty,
			firstName: fromNullable(lead.firstName),
			lastName: fromNullable(lead.lastName),
			fullName: fromNullable(lead.fullName),
			emailAddress: fromNullable(lead.emailAddress),
			age: fromNullable(lead.age),
			agentId: fromNullable(lead.agentId),
			createdDate: fromNullable(lead.createdDate),
			leadStrategy: ObjectUtils.isNotNullable(lead.leadStrategy) ? (lead.leadStrategy.map(parseLeadStrategy)) : ArrayUtils.empty // Array<TLeadStrategyInfoResponse>
		};
	};
}